import { useState, useEffect, useCallback, useRef } from "react";
import axios, { API } from "@/lib/api";

// 🧠 Remembered UI choices per user: localStorage for instant reads, server (/auth/prefs) so they follow the user to any device.
const LS = "sellox_prefs";
let cache = null;
let loaded = false;
let loading = null;
let pending = {};
let flushTimer = null;
const listeners = new Set();

const readLs = () => { try { return JSON.parse(localStorage.getItem(LS) || "{}"); } catch (_e) { return {}; } };
const writeLs = () => { try { localStorage.setItem(LS, JSON.stringify(cache || {})); } catch (_e) {} };

function ensureCache() {
  if (cache === null) cache = readLs();
  return cache;
}

export async function loadPrefs() {
  if (loaded) return ensureCache();
  if (loading) return loading;
  loading = axios.get(`${API}/auth/prefs`).then(r => {
    cache = { ...ensureCache(), ...(r.data || {}) };
    loaded = true; writeLs();
    listeners.forEach(fn => fn());
    return cache;
  }).catch(() => ensureCache()).finally(() => { loading = null; });
  return loading;
}

export function resetPrefs() { cache = null; loaded = false; pending = {}; try { localStorage.removeItem(LS); } catch (_e) {} }

function flush() {
  flushTimer = null;
  const body = pending; pending = {};
  if (Object.keys(body).length) axios.put(`${API}/auth/prefs`, body).catch(() => {});
}

export function setPref(key, value) {
  ensureCache()[key] = value; writeLs();
  pending[key] = value;
  if (flushTimer) clearTimeout(flushTimer);
  flushTimer = setTimeout(flush, 800);
  listeners.forEach(fn => fn(key));
}

export function getPref(key, fallback) {
  const v = ensureCache()[key];
  return v === undefined ? fallback : v;
}

/** const [days, setDays] = usePref("restock.days", 30) — like useState, but remembered for this user. */
export function usePref(key, fallback) {
  const [v, setV] = useState(() => getPref(key, fallback));
  const keyRef = useRef(key); keyRef.current = key;
  useEffect(() => {
    const fn = (k) => { if (!k || k === keyRef.current) setV(getPref(keyRef.current, fallback)); };
    listeners.add(fn);
    if (!loaded) loadPrefs();
    return () => listeners.delete(fn);
  }, [fallback]);
  const set = useCallback((nv) => {
    const next = typeof nv === "function" ? nv(getPref(keyRef.current, fallback)) : nv;
    setPref(keyRef.current, next);
  }, [fallback]);
  return [v, set];
}
